import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { buildSaveFilePreview } from '../../lib/imageEditorGuides';

/** Keep in sync with ImageEditorModal MAX_CANVAS_DIMENSION. */
export const IMAGE_EDITOR_MAX_CANVAS_DIMENSION = 1200;

/**
 * Shared New-canvas size + free filename prompt (File → New in the modal).
 *
 * @param {{
 *   open: boolean,
 *   width: number|string,
 *   height: number|string,
 *   filename?: string,
 *   onWidthChange: (value: string) => void,
 *   onHeightChange: (value: string) => void,
 *   onFilenameChange?: (value: string) => void,
 *   onCancel: () => void,
 *   onCreate: () => void,
 *   busy?: boolean,
 *   title?: string,
 * }} props
 */
export default function ImageEditorNewCanvasDialog({
  open,
  width,
  height,
  filename = 'untitled',
  onWidthChange,
  onHeightChange,
  onFilenameChange,
  onCancel,
  onCreate,
  busy = false,
  title = 'New blank canvas',
}) {
  const preview = buildSaveFilePreview(filename, 'png');
  const canCreate = preview.valid;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.25} sx={{ pt: 0.5 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              autoFocus
              size="small"
              label="Width"
              type="number"
              value={width}
              onChange={(e) => onWidthChange(e.target.value)}
              inputProps={{ min: 1, max: IMAGE_EDITOR_MAX_CANVAS_DIMENSION }}
              fullWidth
              disabled={busy}
            />
            <TextField
              size="small"
              label="Height"
              type="number"
              value={height}
              onChange={(e) => onHeightChange(e.target.value)}
              inputProps={{ min: 1, max: IMAGE_EDITOR_MAX_CANVAS_DIMENSION }}
              fullWidth
              disabled={busy}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Dimensions are clamped to 1–{IMAGE_EDITOR_MAX_CANVAS_DIMENSION}px.
          </Typography>
          {typeof onFilenameChange === 'function' ? (
            <>
              <TextField
                size="small"
                label="Filename"
                value={filename}
                onChange={(e) => onFilenameChange(e.target.value)}
                disabled={busy}
                fullWidth
                placeholder="e.g. hero_banner"
                helperText="Whole name — no forced prefix."
                inputProps={{ 'aria-label': 'Filename' }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace' }}
              >
                {preview.valid
                  ? `Will create: ${preview.fileName}`
                  : 'Enter a filename'}
              </Typography>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onCreate}
          disabled={busy || (typeof onFilenameChange === 'function' && !canCreate)}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * @param {unknown} value
 * @param {number} [max]
 * @returns {number}
 */
export function clampImageEditorCanvasDimension(
  value,
  max = IMAGE_EDITOR_MAX_CANVAS_DIMENSION,
) {
  return Math.max(1, Math.min(max, Math.round(Number(value) || 1)));
}
